/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import _identity from 'lodash/identity';
import _isEmpty from 'lodash/isEmpty';
import _isEqual from 'lodash/isEqual';
import _pickBy from 'lodash/pickBy';
import _uniqBy from 'lodash/unionBy';
import _xorWith from 'lodash/xorWith';
import PropTypes from 'prop-types';

import ZpButton from 'components/zpButton';
import { formatCurrency, importStatic, isUserOutsideUS, JOB_BUCKET_GROUP } from 'utils';
import { MEDIAN_SALARY_THRESHOLD } from 'utils/constant';

import AutosuggestProxy from '../../services/autosuggestProxy';
import { jobService } from '../../services/jobService';
import AutosuggestInput from '../autosuggestInput/index';
import ZpChip from '../ZpChip';

import {
  FILTER_PARAMS,
  FILTER_TYPES,
  mapAutoSuggestValue,
  SHOW_AS,
  WORK_FROM_HOME,
} from './filterUtils';
import SalaryFilter from './SalaryFilter';

import zpFilterClasses from './zpFilter.module.css';

const propTypes = {
  type: PropTypes.string.isRequired,
  param: PropTypes.string.isRequired,
  headerText: PropTypes.string,
  /** Initial button lable. */
  buttonText: PropTypes.string,
  /** Candidate for removal. This prop makes no sense. Use buttonText instead. */
  newButtonText: PropTypes.string,
  /** The deepest element in the filter button. */
  // These values are from the SHOW_AS constant, but propTypes.oneOf() doesn't like constants.
  showAs: PropTypes.oneOf(['button', 'link']),
  /** Right-align the filter dropdown window. */
  alignRight: PropTypes.bool,
  /** Controlled filter open. */
  filterOpen: PropTypes.bool,
  filterGroup: PropTypes.string,
  /** The values available for selecting. Mandatory for multi and single select filters. */
  valueRange: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.any,
      displayName: PropTypes.string,
    })
  ),
  /** The values that are selected for the first render. */
  preselectedItems: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.any,
      displayName: PropTypes.string,
    })
  ),
  /** Various configurations that change filter behavior. */
  options: PropTypes.shape({
    /** AutosuggestInput props. */
    autoSuggest: PropTypes.bool,
    autoSuggestFocus: PropTypes.bool,
    autoSuggestPlaceholder: PropTypes.string,
    /** Autosuggest input container className. */
    autoSuggestOutClass: PropTypes.string,

    /** Tracking log event callback triggered on filter button click. */
    logEvent: PropTypes.any,
    /** Normalized job title display name. */
    jobTitle: PropTypes.string,
    /** Limits the number of selected values for multi-select filters. */
    limit: PropTypes.number,
    /** Error message displayed when the options.limit is reached. */
    errorMessage: PropTypes.string,
    /** Label for the <SalaryFilter /> component. */
    salaryFieldLabel: PropTypes.string,
    /** Whether to show all options for the single select filter, or just the selected ones. */
    showOnlyEnabled: PropTypes.bool,
    /** Hides the filter dropdown overlay. */
    noOverlay: PropTypes.bool,
    /** Hides the filter dropdown footer. */
    noFooter: PropTypes.bool,
    /** Dropdown footer content. */
    footerText: PropTypes.string,
    /** Hides the clear button. */
    noClear: PropTypes.bool,
    /** Needs param === FILTER_PARAMS.EDUCATION and options.footerText to work. */
    educationSwitch: PropTypes.bool,
    /** CSS classes passed to the button component. */
    buttonClassNames: PropTypes.string,

    enabledMoreValues: PropTypes.any,
    searchUnitedStates: PropTypes.any,
    profileJobs: PropTypes.bool,
    duplicateForCompact: PropTypes.bool,
    remote: PropTypes.bool,
    redirectedLoc: PropTypes.any,
  }),
  /** Makes the filter button ignore interactions. */
  disabled: PropTypes.bool,
  /** Removes the filter from the DOM when disabled === true. */
  hiddenOnDisabled: PropTypes.bool,
  /** Filter button lable is static and does not change when a filter is selected. */
  alwaysShowButtonText: PropTypes.bool,
  onApply: PropTypes.func,
  onClear: PropTypes.func,
  onReset: PropTypes.func,
  onUndo: PropTypes.func,
  onFilterClick: PropTypes.func,
  onFilterSelect: PropTypes.func,
  /** Candidate for removal. Prefer to handle this at the page level. */
  showAsworkFromHome: PropTypes.bool,
  children: PropTypes.node,
  /** Controlled list of suggestions for the AutosuggestInput component. */
  staticSuggestions: PropTypes.arrayOf(PropTypes.object),
  /** Makes the filter button active even if no filter is selected. */
  isActive: PropTypes.bool,
  sideBarFilters: PropTypes.bool,
  isMiniSidePanel: PropTypes.bool,
  /** Makes the filter-option of the single filter full width. */
  isSingleColumnSingleFilter: PropTypes.bool,
  showAutoSuggestErrorMessage: PropTypes.bool,
  /**
   * The compact mode is for rendering multiple filters together in other UI elements,
   * like the filter carousel.
   * The filter button and the footer are not rendered, but all the compact
   * filter dropdowns are displayed in a single window.
   */
  isCompact: PropTypes.bool,
};

const defaultProps = {
  headerText: '',
  buttonText: 'Button',
  newButtonText: '',
  alignRight: false,
  showAs: SHOW_AS.BUTTON,
  valueRange: [],
  preselectedItems: [],
  filterEnabledValues: [],
  options: {},
  disabled: false,
  hiddenOnDisabled: false,
  alwaysShowButtonText: false,
  onClear: () => {},
  onApply: () => {},
  onFilterClick: () => {},
  showAsworkFromHome: false,
  children: null,
  staticSuggestions: [],
  isActive: null,
  sideBarFilters: false,
  isMiniSidePanel: false,
  isSingleColumnSingleFilter: false,
  showAutoSuggestErrorMessage: false
};

const ZpFilter = ({
  type,
  param,
  showAs,
  options,
  isCompact,
  headerText,
  buttonText,
  onInputChange,
  newButtonText,
  valueRange,
  filterOpen,
  alignRight,
  filterGroup,
  preselectedItems,
  filterEnabledValues,
  disabled,
  hiddenOnDisabled,
  alwaysShowButtonText,
  onApply,
  onClear,
  onFilterClick,
  onFilterSelect,
  controlledValues,
  showAsworkFromHome,
  staticSuggestions,
  isActive,
  sideBarFilters,
  isMiniSidePanel,
  isSingleColumnSingleFilter,
  showAutoSuggestErrorMessage
}) => {
  const [open, setOpen] = useState(false);

  const [errorMessage, setErrorMessage] = useState('');

  const criteria = useSelector((state) => state.locals.criteria);

  const [filterValues, setFilterValues] = useState(getInitialFilterValues(undefined, criteria));

  const isFilterOpen = filterOpen !== undefined ? filterOpen : open;

  const enabledValues = filterValues.filter((value) => value.enabled);

  const [button, setButtonText] = useState(getButtonText(preselectedItems));

  const [stateHistory, setStateHistory] = useState([]);

  const [sliderValue, setSliderValue] = useState(
    Array.isArray(filterValues[0]?.value)
      ? filterValues[0]?.value?.[0] || 0
      : filterValues[0]?.value || 0
  );

  const [medianSalary, setMedianSalary] = useState(80000);

  const [percentile90, setPercentile90] = useState(200000);

  const [educationSwitch, setEducationSwitch] = useState(options.educationSwitch);

  const [filterDropdownPostion, setfilterDropdownPostion] = useState({ x: 0, y: 0 });

  const setFilterValuesCustom = useCallback((selectedFilterValue, triggered = false) => {
    setFilterValues(selectedFilterValue);

    if (onFilterSelect && (!sideBarFilters || triggered)) {
      const mappedValues = selectedFilterValue
        .slice(0, options?.duplicateForCompact ? 1 : selectedFilterValue.length)
        .filter((v) => v.enabled)
        .map(({ value, displayName }) => ({ value, displayName }));

      onFilterSelect(param, mappedValues);
    }
  }, [onFilterSelect]);

  async function fetchSalaryForTitle() {
    try {
      const estimatedSalary = await jobService.getEstimateSalaryForSalaryFilter({
        title: options.jobTitle,
      });
      setMedianSalary(estimatedSalary.median);
      setPercentile90(estimatedSalary.percentile);
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    if (options.jobTitle && sliderValue === 0 && param === FILTER_PARAMS.SALARY) {
      fetchSalaryForTitle();
    }
  }, []);

  useEffect(() => {
    if (isCompact) {
      setFilterEnabledValues();
    }
  }, []);

  useEffect(() => {
    if (newButtonText) setButtonText(newButtonText);
  }, [newButtonText]);

  useEffect(() => {
    setFilterValuesCustom(_uniqBy([...filterValues, ...valueRange], 'displayName'));
  }, [valueRange]);


  useEffect(() => {
    if (!isCompact && filterGroup && type !== FILTER_TYPES.MORE) {
      setFilterEnabledValues();
    }
  }, [filterEnabledValues]);

  useEffect(() => {
    if (!filterGroup) {
      setFilterValuesCustom(
        _uniqBy(
          [
            ...filterValues,
            ...preselectedItems.map(({ value, displayName }) => ({
              value,
              displayName,
              enabled: true,
            })),
          ],
          'displayName'
        )
      );
    }
    const initialFilterValue = getInitialFilterValues(filterValues, criteria);
    if (initialFilterValue && initialFilterValue[0] && param === FILTER_PARAMS.SALARY) {
      setSliderValue(
        Array.isArray(filterValues[0]?.value)
          ? filterValues[0]?.value?.[0] || 0
          : filterValues[0]?.value || 0
      );
    }
    if (param === FILTER_PARAMS.EDUCATION && preselectedItems.length) {
      const value = Array.isArray(preselectedItems[0].value)
        ? preselectedItems[0].value[0]
        : preselectedItems[0].value;
      setEducationSwitch(value === 'None');
    }
  }, [preselectedItems]);

  useEffect(() => {
    if (type === FILTER_TYPES.MORE) {
      Object.values(options?.enabledMoreValues || {}).forEach((moreValues) => {
        const changed = stateHistory.length ? hasFilterStateChanged(moreValues) : true;
        if (changed) {
          setStateHistory((prevValues) => [...prevValues, moreValues]);
        }
      });
    } else {
      const changed = stateHistory.length ? hasFilterStateChanged() : true;
      if (changed) {
        setStateHistory([...stateHistory, enabledValues]);
      }
    }
  }, [isFilterOpen]);

  useEffect(() => {
    const oldFilterValues = [...filterValues];
    const index = oldFilterValues.findIndex((filterValue) => filterValue.enabled);
    if (index !== -1 && valueRange[index]) {
      oldFilterValues[index] = {
        ...oldFilterValues[index],
        value: educationSwitch
          ? valueRange[index].value === 'None'
            ? valueRange[index].value
            : ['None', valueRange[index].value]
          : options.autoSuggest
          ? oldFilterValues[index].value
          : valueRange[index].value,
      };
      setFilterValuesCustom(oldFilterValues);
    }
  }, [educationSwitch]);

  useEffect(() => {
    if (controlledValues && Array.isArray(controlledValues) && controlledValues.length) {
      const allVals = controlledValues.map((x) => x.value);
      setFilterValuesCustom(
        getInitialFilterValues(controlledValues, criteria).map((x) => ({
          ...x,
          enabled: allVals.includes(x.value) || x.forcedOption,
        }))
      );
      setButtonText(controlledValues[0].displayName ?? buttonText);
    }
  }, [controlledValues]);

  function setFilterEnabledValues() {
    const filterValuesCopy = [...filterValues];
    filterValuesCopy.forEach(({ displayName }, idx) => {
      const index = filterEnabledValues.findIndex(
        (filterValue) => filterValue.displayName === displayName
      );
      if (index !== -1) {
        filterValuesCopy[idx].enabled = true;
      } else {
        if (param === FILTER_PARAMS.SALARY) {
          setSliderValue(0);
        }
        filterValuesCopy[idx].enabled = false;
      }
    });
    const enabled = filterValuesCopy.length ? filterValuesCopy.filter(({ enabled }) => enabled) : filterEnabledValues.map((item) => ({ ...item, enabled: true }));
    setButtonText(getButtonText(enabled));
    setFilterValuesCustom(filterValuesCopy);
  }

  function getInitialFilterValues(newValues, criteria) {
    let newValueRange = valueRange.map((filterValue) => {
      const index = preselectedItems.findIndex(
        (preselectedItem) => preselectedItem.displayName === filterValue.displayName
      );
      if (index !== -1) {
        return {
          ...filterValue,
          enabled: true,
        };
      }
      return filterValue;
    });

    newValueRange = newValueRange.map((filterValue) => {
      const index = filterEnabledValues.findIndex(
        (preselectedItem) => preselectedItem.displayName === filterValue.displayName
      );
      if (index !== -1) {
        return {
          ...filterValue,
          enabled: true,
        };
      }
      return filterValue;
    });

    return _uniqBy(
      [
        ...newValueRange,
        ...(newValues || filterEnabledValues).map(({ value, displayName }) => ({
          value,
          displayName,
          enabled: true,
        })),
        ...// Avoid have pre-selected items when user clears them
        (param === FILTER_PARAMS.LOCATION && filterEnabledValues?.length
          ? []
          : preselectedItems.map(({ value, displayName }) => ({
              value,
              displayName,
              enabled: true,
            }))),
        ...(param === FILTER_PARAMS.LOCATION &&
        options.remote &&
        showAsworkFromHome &&
        !isUserOutsideUS(criteria)
          ? [
              {
                value: WORK_FROM_HOME,
                displayName: WORK_FROM_HOME,
              },
            ]
          : []),
      ],
      'displayName'
    );
  }

  function getButtonText(values) {
    return values.length > 1
      ? `${values[0].displayName}, ...`
      : values.length === 1
      ? values[0].displayName
      : buttonText;
  }

  const hasFilterStateChanged = (newValues = enabledValues) => {
    const lastValues = stateHistory[stateHistory.length - 1];
    return !_isEmpty(_xorWith(lastValues, newValues, _isEqual));
  };

  const handleAutosuggestError = (errorObject) => {
    setErrorMessage(errorObject.errorMessage);
  };

  const handleFilterOpen = (isOpen) => {
    if (filterOpen !== undefined) {
      onFilterClick(param);
    } else {
      setOpen(isOpen);
    }
  };

  const handleApply = (newValues = enabledValues) => {
    if (errorMessage) setErrorMessage('');
    handleFilterOpen(false);
    if (type !== FILTER_TYPES.MORE) {
      const mappedValues = newValues.map(({ value, displayName, redirectedLoc, population }) =>
        _pickBy(
          {
            value,
            displayName,
            redirectedLoc,
            population,
          },
          _identity
        )
      );
      if (hasFilterStateChanged(newValues)) {
        setButtonText(getButtonText(newValues));
        onApply(
          param,
          type === FILTER_TYPES.MULTI ? mappedValues : mappedValues.length ? mappedValues[0] : []
        );
      }
    } else {
      onApply();
    }
  };

  const handleAutosuggestChange = (model) => {
    const { isListOpen, suggestionSelected, displayValue, redirectedLoc, population, valueObj } =
      model;

    if (!isListOpen && !displayValue) {
      return;
    }

    if (enabledValues.length >= options.limit) {
      const defaultErrorMessage = `Limit of ${options.limit} selections. Please remove one before adding another.`;
      setErrorMessage(options.errorMessage || defaultErrorMessage);
      return;
    }
    setErrorMessage('');

    let oldFilterValues = [...filterValues];
    const index = oldFilterValues.findIndex(
      (filterValue) => filterValue.displayName === displayValue
    );

    if (!isListOpen && suggestionSelected) {
      if (index !== -1 && type !== FILTER_TYPES.AUTOSUGGEST) {
        if (!oldFilterValues[index].enabled) {
          oldFilterValues[index].enabled = true;
          if (type === FILTER_TYPES.SINGLE) {
            oldFilterValues = oldFilterValues.map((filterValue, idx) => {
              if (index !== idx && filterValue.enabled) {
                filterValue.enabled = false;
              }
              return filterValue;
            });
          }
          setFilterValuesCustom(oldFilterValues, true);
        }
        return;
      }
      const value = mapAutoSuggestValue(param, valueObj);
      if (type === FILTER_TYPES.SINGLE || type === FILTER_TYPES.AUTOSUGGEST) {
        if (onInputChange) {
          if (!value.normalizedTitle) {
            setErrorMessage(options.defaultErrorMessage);
          } else {
            onInputChange(value);
          }
          return;
        }
        if (isCompact && options?.duplicateForCompact) {
          const newFilterValues = [
            {
              value,
              displayName: displayValue,
              ...(redirectedLoc ? { redirectedLoc, population } : {}),
              enabled: true,
            },
          ];
          setFilterValuesCustom(newFilterValues, true);
          return;
        }
        handleSingleSelect({
          value,
          displayName: displayValue,
          ...(redirectedLoc ? { redirectedLoc, population } : {}),
        });
      } else {
        const newFilterValues = [
          {
            value,
            displayName: displayValue,
            ...(redirectedLoc ? { redirectedLoc, population } : {}),
            enabled: true,
          },
          ...filterValues,
        ];
        setFilterValuesCustom(newFilterValues, true);
      }
    }
  };

  const handleSingleSelect = (item) => {
    let oldFilterValues = [...filterValues];
    const currentIndex = oldFilterValues.findIndex(
      (filterValue) => filterValue.displayName === item.displayName
    );
    if (currentIndex !== -1 && !oldFilterValues[currentIndex].enabled) {
      const prevIndex = oldFilterValues.findIndex((filterValue) => filterValue.enabled);
      if (prevIndex !== -1) {
        oldFilterValues[prevIndex] = { ...oldFilterValues[prevIndex], enabled: false };
        }
      oldFilterValues[currentIndex] = {
        ...oldFilterValues[currentIndex],
        value:
          educationSwitch && oldFilterValues[currentIndex].value !== 'None'
            ? ['None', valueRange[currentIndex].value]
            : Array.isArray(oldFilterValues[currentIndex].value) &&
              oldFilterValues[currentIndex].value[0] === 'None'
            ? oldFilterValues[currentIndex].value[1]
            : oldFilterValues[currentIndex].value,
        enabled: true,
      };
    } else if (item.enabled) {
      const currentEnabledItemIndex = oldFilterValues.findIndex(
        (filterValue) => filterValue.enabled
      );
      oldFilterValues[currentEnabledItemIndex] = {
        ...oldFilterValues[currentEnabledItemIndex],
        enabled: false,
      };
      } else {
      const currentEnabledItemIndex = oldFilterValues.findIndex(
        (filterValue) => filterValue.enabled
      );
      if (currentEnabledItemIndex !== -1) {
        oldFilterValues[currentEnabledItemIndex] = {
          ...oldFilterValues[currentEnabledItemIndex],
          enabled: false,
        };
      }
      oldFilterValues = [
        {
          ...item,
          enabled: true,
        },
        ...oldFilterValues,
      ];
    }
    if (item.value === 'None') {
      setEducationSwitch(true);
    }
    const newFilterValues = _uniqBy(oldFilterValues, 'displayName');
    const instantFilterValues = newFilterValues
      .filter((value) => value.enabled)
      .map(({ value, displayName }) => ({ value, displayName }));
    if (
      (((options.autoSuggest && type === FILTER_TYPES.SINGLE) || type === FILTER_TYPES.AUTOSUGGEST) &&
      instantFilterValues.length) ||
      options.noFooter
    ) {
      instantApply(instantFilterValues);
    }
    setFilterValuesCustom(newFilterValues, true);
  };

  const instantApply = (instantFilterValues) => {
    if (errorMessage) setErrorMessage('');
    handleFilterOpen(false);
    setButtonText(getButtonText(...[instantFilterValues]));
    onApply(
      param,
      type === FILTER_TYPES.MULTI
        ? instantFilterValues
        : instantFilterValues.length
        ? instantFilterValues[0]
        : []
    );
  };

  const handleButtonClick = (e) => {
    if(isMiniSidePanel){
      let btnPosition = e?.target?.closest("button")?.getBoundingClientRect();
      if(btnPosition){
        setfilterDropdownPostion({
          x : btnPosition?.x,
          y : btnPosition?.y + btnPosition?.height
        });
      }
    }

    if (!disabled) {
      if (options.logEvent && !filterOpen) {
        options.logEvent();
      }
      handleFilterOpen(!open);
    }
  };

  const handleMultiSelect = (item) => {
    if (enabledValues.length >= options.limit && !item.enabled) {
      const defaultErrorMessage = `Limit of ${options.limit} items. Please remove one before selecting another.`;
      setErrorMessage(options.errorMessage || defaultErrorMessage);
      return;
    }
    setErrorMessage('');

    const oldFilterValues = [...filterValues];
    const index = oldFilterValues.findIndex(
      (filterValue) => filterValue.displayName === item.displayName
    );
    if (index !== -1) {
      if (item.enabled) {
        oldFilterValues[index] = { ...oldFilterValues[index], enabled: false };
      } else {
        oldFilterValues[index] = { ...oldFilterValues[index], enabled: true };
      }
    }
    if (errorMessage) {
      setErrorMessage('');
    }
    setFilterValuesCustom(oldFilterValues, true);
  };

  const handleClear = () => {
    if (errorMessage) setErrorMessage('');
    const oldFilterValues = [...filterValues].map(({ value, displayName }) => ({
      value,
      displayName,
    }));
    if (param === FILTER_PARAMS.SALARY) {
      setSliderValue(0);
    }
    handleFilterOpen(false);
    const newEnabledValues = [...filterValues].filter((value) => value.enabled);
    setButtonText(getButtonText([]));
    setFilterValuesCustom(oldFilterValues, true);
    if (type === FILTER_TYPES.MORE) {
      onClear();
    }
    if (newEnabledValues.length) {
      onClear(param, []);
    }
  };

  const handleOverlayClick = () => {
    if (type === FILTER_TYPES.MORE) {
      const filterChanges = Object.values(options?.enabledMoreValues || {}).map((moreValues) =>
        hasFilterStateChanged(moreValues)
      );
      if (filterChanges.some((isChanged) => isChanged)) {
        handleApply();
      }
      handleFilterOpen(false);
    } else if (hasFilterStateChanged()) {
      handleApply();
    } else {
      handleFilterOpen(false);
    }
  };

  const handleSalaryChange = (salary) => {
    setSliderValue(salary);
    setFilterValuesCustom([
      {
        value: medianSalary < MEDIAN_SALARY_THRESHOLD ? salary * 2080 : salary,
        displayName: medianSalary < MEDIAN_SALARY_THRESHOLD ? `$${salary}` : formatCurrency(salary),
        enabled: true,
      },
    ], true);
  };

  const handleEducationSwitchChange = (e) => {
    setEducationSwitch(e.target.checked);
  };

  const onEnterKeyDown = (input, matches) => async () => {
    const matchesLength = matches.length;
    if (param === FILTER_PARAMS.JOB_TITLE && options.profileJobs && matchesLength <= 1) {
      const { getAutosuggestTitles } = AutosuggestProxy;

      if (!input || !input.displayValue) return;

      const searchValue = input.displayValue;
      const results = await getAutosuggestTitles(searchValue);
      const resultTitle = results.length > 0 ? results[0].name || results[0].value : '';
      const searchTextFinal =
        searchValue.toLowerCase() === resultTitle.toLowerCase() ? resultTitle : searchValue;

      const newValue = {
        value: mapAutoSuggestValue(param, {
          name: resultTitle,
          original_name: searchValue,
        }),
        displayName: searchValue,
        enabled: true,
      };

      const oldFilterValues = [...filterValues];

      const index = oldFilterValues.findIndex(
        (filterValue) => filterValue.displayName === newValue.displayName
      );

      if (searchTextFinal && resultTitle && index === -1) {
        setFilterValuesCustom([newValue, ...filterValues], true);
      } else if (index !== -1) {
        if (oldFilterValues[index].enabled) {
          oldFilterValues[index] = { ...oldFilterValues[index], enabled: false };
        } else {
          oldFilterValues[index] = { ...oldFilterValues[index], enabled: true };
        }

        setFilterValuesCustom(oldFilterValues, true);
      }
    }
  };

  const getAutosuggestPrefill = () => {
    if (type === FILTER_TYPES.AUTOSUGGEST) {
      return filterEnabledValues?.[0]?.displayName ?? preselectedItems?.[0]?.displayName;
    }

    return '';
  }

  const renderFilterContent = () => (
    <>
      <div className={zpFilterClasses['filter-header']}>
        {param === FILTER_PARAMS.SALARY && medianSalary < MEDIAN_SALARY_THRESHOLD
          ? 'Minimum Salary Desired Per Hour'
          : headerText}
      </div>

      <section className={zpFilterClasses['filter-body']}>
        {options.autoSuggest && (
          <AutosuggestInput
            category={param}
            inputProps={{
              className: 'typeahead form-control filter-input',
              name: param,
              ...(options.autoSuggestPlaceholder
                ? {
                    placeholder: options.autoSuggestPlaceholder,
                  }
                : {}),
            }}
            staticSuggestions={
              [FILTER_PARAMS.COMPANY_INDUSTRY, FILTER_PARAMS.DEPARTMENT].includes(param)
                ? staticSuggestions
                : []
            }
            redirectedLoc={options.redirectedLoc}
            prefillValue={getAutosuggestPrefill()}
            autoFocus={options.autoSuggestFocus !== false || !!staticSuggestions?.length}
            clearOnSelect={type !== FILTER_TYPES.AUTOSUGGEST}
            onChange={handleAutosuggestChange}
            onErrorCallback={handleAutosuggestError}
            extended
            searchUnitedStates={options.searchUnitedStates}
            onEnterKeyDown={onEnterKeyDown}
            freeTextSupported={options.profileJobs}
          />
        )}

        {param === FILTER_PARAMS.SALARY && (
          <SalaryFilter
            label={options.salaryFieldLabel}
            sliderValue={sliderValue}
            isHourly={medianSalary < MEDIAN_SALARY_THRESHOLD}
            percentile90={percentile90}
            onSalaryChange={(salary) => handleSalaryChange(salary)}
          />
        )}

        {errorMessage && <p className="z-font-15 red-text font-weight-600 m-0">{errorMessage}</p>}

        <section className={zpFilterClasses['filter-items']}>
          {type === FILTER_TYPES.SINGLE &&
            param !== FILTER_PARAMS.SALARY &&
            filterValues
              .filter((item) => (options.showOnlyEnabled ? item.enabled : true))
              .map((item, idx) => (
                <div
                  key={`single-select-option-${item.displayName}-${idx}`}
                  className={classNames(
                    'filter-option',
                    { enable: item.enabled },
                    { 'single-column-option': isSingleColumnSingleFilter },
                  )}
                  onClick={() => handleSingleSelect(item)}
                >
                  {item.displayName}
                </div>
              ))}

          {type === FILTER_TYPES.MULTI &&
            filterValues.map((item, idx) => (
              <ZpChip
                key={`multi-select-option-${item.displayName}-${idx}`}
                display={item.displayName}
                enable={item.enabled}
                data={item}
                onClick={() => handleMultiSelect(item)}
              />
            ))}
        </section>

        {options.footerText && (
          <div className={zpFilterClasses['footer-text']}>
            {param === FILTER_PARAMS.EDUCATION && (
              <label className={classNames('switch', { checked: educationSwitch })}>
                <input
                  type="checkbox"
                  checked={educationSwitch}
                  onChange={handleEducationSwitchChange}
                />
                <span className="slider round" />
              </label>
            )}
            <span
              className={classNames(
                'white-space-normal',
                { 'ml-3': param !== FILTER_PARAMS.EDUCATION }
              )}
            >
              {options.footerText}
            </span>
          </div>
        )}
      </section>
    </>
  );

  if (disabled && hiddenOnDisabled) {
    return null;
  }

  if (!(isCompact && options?.duplicateForCompact) && type === FILTER_TYPES.AUTOSUGGEST) {
    const prefillValue = filterEnabledValues?.[0]?.displayName ?? preselectedItems?.[0]?.displayName;

    return (
      <div
        id="zp-filter"
        data-button-text={buttonText}
        className={classNames(
          zpFilterClasses['zp-filter-container'],
          zpFilterClasses['filter-auto-suggest-input'],
          'mr-2 position-unset',
          options.autoSuggestOutClass,
          { [zpFilterClasses['filter-sidebar']]: sideBarFilters }
        )}
      >
        <AutosuggestInput
          key={`${param}-${prefillValue}`}
          category={param}
          inputProps={{
            className: sideBarFilters ? 'filter-input' : 'typeahead zp-input zp-input-search',
            name: param,
            ...(options.autoSuggestPlaceholder
              ? {
                  placeholder: options.autoSuggestPlaceholder,
                }
              : {}),
          }}
          prefillValue={filterEnabledValues?.[0]?.displayName ?? preselectedItems?.[0]?.displayName}
          redirectedLoc={options.redirectedLoc}
          onChange={handleAutosuggestChange}
          extended
          searchUnitedStates={options.searchUnitedStates}
          onEnterKeyDown={onEnterKeyDown}
          onErrorCallback={handleAutosuggestError}
        />
        {errorMessage && showAutoSuggestErrorMessage && (
          <span className={classNames(
            zpFilterClasses["auto-suggest-error-message"],
            "d-block m-0 z-font-15 font-600 red-text font-weight-600 z-popup-overlay"
          )}>
            {errorMessage}
          </span>
        )}
      </div>
    );
  }

  return (
    <div
      id="zp-filter"
      data-button-text={buttonText}
      data-open={isFilterOpen}
      className={classNames(zpFilterClasses['zp-filter-container'], 'zp-filter-container', {
        [zpFilterClasses['filter-sidebar']]: sideBarFilters,
      })}
    >
      {isFilterOpen && !isCompact && (
        <div
          className={classNames(
            zpFilterClasses['filters-background-overlay'],
            options.noOverlay ? zpFilterClasses['no-overlay'] : ''
          )}
          onClick={handleOverlayClick}
        />
      )}

      {!isCompact && (
        <ZpButton
          theme={buttonText !== button || isActive ? 'primary' : 'secondary'}
          dropdownStatus={showAs !== SHOW_AS.LINK && (isFilterOpen ? 'open' : 'closed')}
          className={
            // .zpBtn.zp-filter-link -> theme: 'link'
            classNames('zpBtn', options.buttonClassNames, {
              [`${zpFilterClasses['zp-filter-link']} zp-filter-link`]: showAs === SHOW_AS.LINK,
              [zpFilterClasses['zp-filter-button']]: showAs !== SHOW_AS.LINK,
              [zpFilterClasses['zp-filter-matches']]:
                filterGroup === JOB_BUCKET_GROUP.MY_PROFILE_JOBS,
              active: buttonText !== button || isActive,
              open: isFilterOpen,
            })
          }
          onClick={handleButtonClick}
          overflowHidden
        >
          {showAs === SHOW_AS.LINK ? (
            <a
              className={classNames(zpFilterClasses['filter-text'], 'list-link', {
                disabled: disabled,
              })}
            >
              {alwaysShowButtonText ? buttonText : button}
            </a>
          ) : alwaysShowButtonText ? (
            buttonText
          ) : (
            button
          )}
        </ZpButton>
      )}

      {isFilterOpen && (
        <div
          className={classNames(
            zpFilterClasses['zp-filter-dropdown'],
            { 'align-right': alignRight },
            { compact: isCompact },
            { 'position-fixed': isMiniSidePanel }
          )}
          style={
            isMiniSidePanel
              ? {
                  top: filterDropdownPostion?.y + 5,
                  left: filterDropdownPostion?.x,
                }
              : undefined
          }
          data-param={param}
        >
          <div className={zpFilterClasses['zp-dropdown-menu']}>
            {!isCompact && (
              <menu
                className={classNames(
                  'd-flex d-lg-none justify-content-between align-items-center my-0',
                  zpFilterClasses['filter-toolbar']
                )}
              >
                <img
                  src={importStatic('/ui-router/images/select-x-dark.svg')}
                  alt="cross-icon"
                  onClick={handleOverlayClick}
                />
                {!options.noClear && <div onClick={handleClear}>Clear</div>}
              </menu>
            )}

            <div className={zpFilterClasses['filter-content']}>{renderFilterContent()}</div>

            {!isCompact && !options.noFooter && (
              <div
                className={classNames(zpFilterClasses['filter-footer'], {
                  'justify-content-end': options.noClear,
                })}
              >
                {!options.noClear && (
                  <div
                    onClick={handleClear}
                    className={classNames(
                      zpFilterClasses['filter-footer-start'],
                      'd-none d-lg-block '
                    )}
                  >
                    Clear
                  </div>
                )}

                <div className={zpFilterClasses['filter-footer-end']} onClick={() => handleApply()}>
                  Apply
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

ZpFilter.propTypes = propTypes;
ZpFilter.defaultProps = defaultProps;

export default ZpFilter;
